import { UserCog, X } from "lucide-react";
import { http, setStoredToken } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

/**
 * Sticky banner shown across the whole app whenever an admin is
 * impersonating another user. Backed by `auth/me.impersonating === true`.
 */
export default function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  if (!user || user === false || !user.impersonating) return null;

  const stop = async () => {
    try {
      // Backend returns the original admin's session token so we can
      // swap it back into localStorage. Without this swap the
      // impersonation token keeps flowing on the next request and
      // /auth/me would still resolve to the impersonated user.
      const { data } = await http.post("/auth/stop-impersonating");
      if (data?.session_token) {
        setStoredToken(data.session_token);
      }
      await refresh?.();
      toast.success("Returned to your admin session");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not end impersonation");
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 bg-[#1C1C1C] text-white border-b border-sage-300/30"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between gap-3 text-[12px]">
        <div className="inline-flex items-center gap-2 min-w-0">
          <UserCog className="w-3.5 h-3.5 text-sage-300 shrink-0" />
          <span className="truncate">
            <span className="text-sage-300 uppercase tracking-[0.18em] text-[10px] mr-2">Impersonating</span>
            <strong>{user.email}</strong>
            {user.impersonator_email && (
              <span className="text-white/60"> · admin: {user.impersonator_email}</span>
            )}
          </span>
        </div>
        <button
          data-testid="impersonation-stop-btn"
          onClick={stop}
          className="inline-flex items-center gap-1 text-sage-300 hover:text-white border border-sage-300/40 hover:border-white/40 rounded-md h-7 px-2.5 transition-colors shrink-0"
        >
          <X className="w-3 h-3" /> Stop & return
        </button>
      </div>
    </div>
  );
}
